// @flow

import React, { useState } from 'react';
import type { Node } from 'react';
import { useDispatch } from 'react-redux';
import find from 'lodash/find';
import get from 'lodash/get';
import { useIntl } from 'react-intl';
import { ArtCard } from '@riseart/art';
import { Heading, Link, Paragraph, MediaQuery } from '@riseart/common';
import {
  units as ENUM_UNITS,
  art as ENUM_ART,
  cart as ENUM_CART,
  analytics as ENUM_ANALYTICS,
} from 'Enum';
import {
  components as CONFIG_COMPONENTS,
  artDirection as CONFIG_ART_DIRECTION,
  application as CONFIG_APP,
} from 'Config';
import { formatCurrency } from 'shared_services/riseart/utils/Utils';
import {
  formatDimensionsLabel,
  renderViewInRoomGridPicture,
} from 'shared_services/riseart/utils/artDataUtils';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { AddCartItemButton } from 'shared_data/providers/queries/AddCartItem';
import { generatePicturePropsForCDN } from 'shared_components/common/artdirection/picture/Picture';
import { FavoriteButton } from 'shared_data/providers/queries/FavoriteButton';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { ART_DIRECTION_SIZES } from 'shared_models/ArtDirectionSizes';
import { gtmElementClicked } from 'shared_data/providers/google/tagmanager/Actions';
import { CartActionRentButton } from 'shared_components/cart/actions/RentButton';

import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';

const IS_OPENED = true;
const { fallbackImages: FALLBACK_IMAGES_LIST } = CONFIG_COMPONENTS.art;
const { main: ART_DIRECTION_CONFIG } = CONFIG_ART_DIRECTION.art;
const DISPLAY_STYLES_WITH_RELATIVE_FAV_POSITION = [1, 3];

type Props = {
  displayStyle?: number | string,
  openInNewTab: boolean,
  locale: Object,
  data: {
    id: number,
    skuBuyId: Number,
    skuRentId: NUmber,
    status: number,
    medium: string,
    title: string,
    width: number,
    height: number,
    unit: string,
    artistId: number,
    artistName: string,
    artistUrl: string,
    skuBuyPrice: string,
    skuRentPrice: ?string,
    url: string,
    store: {
      code: string,
    },
    file: {
      square: {
        height: number,
        width: number,
        url: string,
      },
      fluid: {
        height: number,
        width: number,
        url: string,
      },
    },
    collectionDescription: string,
    isSoldOut: boolean,
    isEnquire: boolean,
    isProduct: boolean,
    isRented: boolean,
    canBuy: boolean,
    canRent: boolean,
    is3D: boolean,
  },
  unitSystem?: ?string,
  imageType: string,
  target?: string,
  toggleArtTabHandler?: Function,
  lazyload: Object,
  config?: Object,
  cardProps?: Object | null,
  onImageLoad?: Function,
  additionalActionControls?: Node,
  showAddToCart?: boolean,
  showRent?: boolean,
  showHoverContent?: boolean,
  onItemClick?: Function,
  disableHeadingTags?: boolean,
};

/**
 * handleOpenArtInTab
 *
 * @param {string} url
 * @param {string} target
 * @param {Function} toggleArtTabHandler
 * @returns {Function}
 */
function handleOpenArtInTab(
  data: Object,
  toggleArtTabHandler: Function,
  preRedirectHandler?: Function,
): Function {
  return (e: Event): void => {
    e.preventDefault();

    const openedWindow = LocationManager.redirect(data.url, { target: data.target });

    if (typeof preRedirectHandler === 'function') {
      preRedirectHandler();
    }

    /*
      in some cases, browsers might return null from window.open(),
      especially when there is a popup blocker, so in this case
      we skip the event listeners for the new tab
    */
    if (!openedWindow) {
      toggleArtTabHandler(data, IS_OPENED);
      LocationManager.redirect(data.url);

      return;
    }

    let windowInitialized = false;

    // fix for IE11 issue: openedWindow does not have addEventListener for IE11 at first
    // IE requires some time to add addEventListener to the window object
    while (windowInitialized === false) {
      if (typeof openedWindow.addEventListener === 'function') {
        windowInitialized = true;
      }
    }

    // called handler from HOC (Google Analytics Event is triggered)
    toggleArtTabHandler(data, IS_OPENED);
    openedWindow.addEventListener('beforeunload', () => toggleArtTabHandler(data));
  };
}

/**
 * ArtItem
 *
 * @param {object} props
 * @returns {Node}
 */
export function ArtItem({
  data,
  locale,
  unitSystem = CONFIG_APP.units.defaultSystem,
  additionalActionControls = null,
  showAddToCart = false,
  showRent = false,
  openInNewTab = false,
  displayStyle = 1,
  imageType,
  target = '_blank',
  toggleArtTabHandler = () => {},
  lazyload,
  onImageLoad,
  showHoverContent = false,
  disableHeadingTags = false,
  onItemClick,
  cardProps = null,
}: Props): Node {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const {
    id,
    is3D,
    skuBuyId,
    skuRentId,
    medium,
    title,
    width,
    height,
    unit,
    artistName,
    artistUrl,
    skuBuyPrice,
    skuRentPrice,
    url,
    store: { code: storeCode },
    file,
    isSoldOut,
    isEnquire,
    isProduct,
    isRented,
    canBuy,
    canRent,
    collectionDescription,
  } = data;
  const lengthUnit = CONFIG_APP.units[unitSystem].length;
  const foundImage = get(
    find(file, (img) => img[imageType]),
    `${imageType}.file`,
  );
  const imgFile: Object =
    foundImage && foundImage.key
      ? foundImage
      : find(FALLBACK_IMAGES_LIST, {
          type:
            imageType === ENUM_ART.list.LAYOUT_FLUID
              ? ENUM_ART.image.type.TYPE_MAIN_MASTER
              : ENUM_ART.image.type.TYPE_MAIN_SQUARE,
        });
  const masterImage = get(
    find(file, (img: Object) => img && img.fl),
    `fl.file`,
  );

  const pictureProperties = generatePicturePropsForCDN({
    image: imgFile,
    sizesList: ART_DIRECTION_SIZES.grid.fluid,
    artDirectionConfig: ART_DIRECTION_CONFIG,
  });

  const trackedData = {
    target,
    referrer: LocationManager.get('href'),
    url: `${LocationManager.get('origin')}${url}`,
  };

  const linkTitle = intl.formatMessage(
    { id: 'components.art.artwork_by_artist' },
    { title, artist: artistName },
  );

  const handleAddToCartClick = () =>
    dispatch(
      gtmElementClicked(
        ENUM_ANALYTICS.element.location.filterArtList,
        ENUM_ANALYTICS.element.action.addToCart,
        ENUM_ANALYTICS.element.object.art,
        trackedData.url,
      ),
    );

  let rightControls = null;

  if (showAddToCart && canBuy) {
    rightControls = (
      <AddCartItemButton
        skuId={skuBuyId}
        cartId={ENUM_CART.type.TYPE_BUY}
        qty={1}
        onLoading={setLoading}
        onClick={handleAddToCartClick}
        openSidebar
      />
    );
  } else if (showRent && canRent) {
    rightControls = (
      <CartActionRentButton
        artId={id}
        skuRentId={skuRentId}
        storeCode={storeCode}
        onLoading={setLoading}
      />
    );
  }

  return (
    <IsomorphicRipple isActive={loading}>
      <ArtCard
        disableHeadingTags={disableHeadingTags}
        showImagePlaceholder
        onArtworkClick={
          openInNewTab
            ? handleOpenArtInTab(trackedData, toggleArtTabHandler, onItemClick)
            : onItemClick
        }
        displayStyle={displayStyle}
        externalLink={openInNewTab}
        linkProps={{ title: linkTitle }}
        hoverContent={
          showHoverContent &&
          renderViewInRoomGridPicture({ is3D, width, height, image: masterImage }, linkTitle)
        }
        pictureProps={
          pictureProperties && {
            ...pictureProperties,
            imgProps: {
              alt: linkTitle,
              onLoad: onImageLoad,
            },
            image: {
              width: imgFile.width,
              height: imgFile.height,
            },
            lazyload,
          }
        }
        target={openInNewTab && target}
        artworkUrl={url}
        artworkTitle={title}
        artistUrl={artistUrl}
        artistName={artistName}
        price={
          canBuy && skuBuyPrice
            ? formatCurrency(skuBuyPrice, storeCode, undefined, {
                precision: 0,
                locale: locale && locale.name,
              })
            : null
        }
        rentPrice={
          canRent && skuRentPrice
            ? `${intl.formatMessage({ id: 'components.art.rent_for' })}
              ${formatCurrency(skuRentPrice, storeCode, undefined, {
                precision: 0,
                locale: locale && locale.name,
              })}/${intl.formatMessage({
                id: 'common.durations.month.abbreviated',
              })}`
            : null
        }
        additionalContent={
          <React.Fragment>
            {medium ? intl.formatMessage({ id: `filters.art.medium.${medium}` }) : null}
            {medium ? ' - ' : ''}
            {formatDimensionsLabel(
              [height, width],
              (unit && ENUM_UNITS[unit.toLowerCase()].long) || '',
              {
                type: 'short',
                convertTo: lengthUnit.unit,
                precision: lengthUnit.precision.low || 0,
              },
            )}
          </React.Fragment>
        }
        overlay={
          collectionDescription && (
            <React.Fragment>
              <MediaQuery minWidth={raScreenLg}>
                {(isScreenLarge) => {
                  return (
                    <Heading tag="h5" level="5">
                      <Link
                        type="light"
                        external
                        href={artistUrl}
                        title={artistName}
                        {...(isScreenLarge ? { target: '_blank' } : null)}
                      >
                        {artistName}
                      </Link>
                    </Heading>
                  );
                }}
              </MediaQuery>
              <Paragraph>{collectionDescription}</Paragraph>
            </React.Fragment>
          )
        }
        status={
          isProduct &&
          !isEnquire &&
          !isRented &&
          isSoldOut &&
          intl.formatMessage({ id: 'components.art.sold' })
        }
        actionControls={
          <React.Fragment>
            <FavoriteButton
              artId={id}
              position={
                DISPLAY_STYLES_WITH_RELATIVE_FAV_POSITION.indexOf(displayStyle) > -1
                  ? 'relative'
                  : 'absolute'
              }
            />
            {additionalActionControls}
          </React.Fragment>
        }
        rightActionControls={rightControls}
        {...(cardProps || {})}
      />
    </IsomorphicRipple>
  );
}
